import { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { BACKEND_URL } from '@const/env'
import { ScormVotes } from '@models/scorm'
import BackButton from '@organisms/BackButton'
import GrayButton from '@organisms/GrayButton'
import { isMobile } from '@const/device'
import { useViewScormMutation } from '@services/scorms'
import GradeBlock from '../GradeBlock'
import NavigationBlock from '../NavigationBlock'
import ExpandButton from '../ScormViewer/ExpandButton'
import {
  BottomWrapper,
  NavButtonsWrapper,
  Wrapper,
  MobileTopWrapper,
  MobileContentWrapper,
  BottomMobileContentWrapper,
  MobileNavBlockWrapper,
  DesktopExpandInModalWrapper,
  MobileExpandInModalWrapper,
} from '../ScormViewer/styled'
import { PdfFrame } from './styled'

type Props = {
  filePath: string
  inModal: boolean
  toggleInModal: Dispatch<SetStateAction<boolean>>
  prevRoute?: string | null
  nextRoute?: string | null
  onChangePage: () => Promise<void>
  scormId: string
  scormIri: string | undefined
  currentVote: ScormVotes | null | undefined
  handleBack: () => void
  isClientCourse?: boolean
  themeId: string
  isTestBlocked?: boolean
  onBlockedNavigation?: (pendingNavigation?: () => void) => void
  showControlQuestionButton?: boolean
  onControlQuestion?: () => void
  isPreviewMode?: boolean
}

const renderGradeBlock = (
  isClientCourse: boolean,
  scormId: string,
  currentVote: ScormVotes | null | undefined
) =>
  isClientCourse ? null : (
    <GradeBlock scormId={scormId} currentVote={currentVote} />
  )

const PdfViewer: FC<Props> = ({
  filePath,
  inModal,
  toggleInModal,
  prevRoute,
  nextRoute,
  onChangePage,
  scormId,
  scormIri,
  currentVote,
  handleBack,
  isClientCourse = false,
  themeId,
  isTestBlocked,
  onBlockedNavigation,
  showControlQuestionButton,
  onControlQuestion,
  isPreviewMode,
}) => {
  const landscape = useMediaQuery({ query: '(orientation: landscape)' })
  const [viewScorm] = useViewScormMutation()
  const securePdfParams = '#toolbar=0&navpanes=0&zoom=page-width&view=FitH'

  useEffect(() => {
    if (scormIri && !isPreviewMode) {
      viewScorm(scormIri)
    }
  }, [viewScorm, scormIri, isPreviewMode])

  if (!filePath && !isClientCourse) return null

  // Build PDF URL - use secure route for internal courses, direct path for platform courses
  const pdfUrl = isClientCourse
    ? `${BACKEND_URL}/secure/course-theme/pdf/${themeId}${securePdfParams}`
    : `${BACKEND_URL}${filePath}${securePdfParams}`

  const back = (
    <BackButton onClick={handleBack}>
      {isPreviewMode ? 'Назад' : 'Весь курс'}
    </BackButton>
  )
  const frame = (
    <PdfFrame
      frameBorder="0"
      name="pdfBlock"
      id="pdfFrame"
      src={pdfUrl}
      inModal={inModal}
      isLandscape={landscape}
    />
  )
  const expand = (
    <ExpandButton inModal={inModal} toggleInModal={toggleInModal} />
  )
  const controlQuestionButton = showControlQuestionButton ? (
    <GrayButton
      onClick={onControlQuestion}
      leftIcon={isMobile ? <QuestionCircleOutlined /> : undefined}
    >
      {isMobile ? '' : 'Ответить на контрольный вопрос'}
    </GrayButton>
  ) : null
  const gradeBlock = renderGradeBlock(isClientCourse, scormId, currentVote)
  const navigation = (
    <NavigationBlock
      onChangePage={onChangePage}
      prevRoute={prevRoute}
      nextRoute={nextRoute}
      isTestBlocked={isTestBlocked}
      onBlockedNavigation={onBlockedNavigation}
      isPreviewMode={isPreviewMode}
    />
  )
  return (
    <Wrapper inModal={inModal} isLandscape={landscape}>
      {isMobile ? (
        <MobileContentWrapper inModal={inModal}>
          {!inModal && <MobileTopWrapper>{back}</MobileTopWrapper>}
          {frame}
          {inModal ? (
            <MobileExpandInModalWrapper>{expand}</MobileExpandInModalWrapper>
          ) : (
            <BottomMobileContentWrapper>
              <MobileNavBlockWrapper>
                {controlQuestionButton}
                {expand}
                {navigation}
              </MobileNavBlockWrapper>
            </BottomMobileContentWrapper>
          )}
        </MobileContentWrapper>
      ) : (
        <>
          {frame}
          {inModal ? (
            <DesktopExpandInModalWrapper>{expand}</DesktopExpandInModalWrapper>
          ) : (
            <BottomWrapper>
              {back}
              {/* Оценка занятия — в правой группе, рядом с «Развернуть» (как на проде).
                  Отдельным элементом BottomWrapper она из-за space-between уезжала в
                  центр. См. тот же порядок в ScormViewer. */}
              <NavButtonsWrapper>
                {gradeBlock}
                {controlQuestionButton}
                {expand}
                {navigation}
              </NavButtonsWrapper>
            </BottomWrapper>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default PdfViewer
